import { ExternalLink, ChevronDown, Activity, Layers, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getLangfuseSessionUrl, getLangfuseTraceUrl } from "@/ai-coding-outputs/langfuseUrl";

interface ViewRunDropdownProps {
  mastraRunUrl: string;
  langfuseTraceId: string | null;
  encounterId?: string | null;
  variant?: "outline" | "ghostOnPrimary";
  onApprove?: () => void;
  isApproved?: boolean;
}

export function ViewRunDropdown({
  mastraRunUrl,
  langfuseTraceId,
  encounterId,
  variant = "outline",
  onApprove,
  isApproved = false,
}: ViewRunDropdownProps) {
  const directTraceUrl = langfuseTraceId ? getLangfuseTraceUrl(langfuseTraceId) : null;
  const sessionUrl = encounterId ? getLangfuseSessionUrl(encounterId) : null;
  // Before the workflow completes we don't have a direct trace URL yet, so fall back to the
  // Langfuse session (keyed by encounter id) — the in-progress trace lives there.
  const viewTraceUrl = directTraceUrl ?? sessionUrl;
  // Only expose the session entry separately when the trace link is a direct trace URL,
  // otherwise it would duplicate the View Trace primary action.
  const viewSessionUrl = directTraceUrl ? sessionUrl : null;
  const hasDropdownItems = Boolean(viewTraceUrl || viewSessionUrl || onApprove);

  if (!hasDropdownItems) {
    return (
      <Button variant={variant} size="sm" className="cursor-pointer" asChild>
        <a href={mastraRunUrl} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">View Run</span>
        </a>
      </Button>
    );
  }

  // View Trace is the default action; fall back to View Run when there's no trace link yet.
  const primaryAction = viewTraceUrl
    ? { href: viewTraceUrl, Icon: Activity, label: "View Trace" }
    : { href: mastraRunUrl, Icon: ExternalLink, label: "View Run" };

  return (
    <div className="flex items-center">
      <Button variant={variant} size="sm" className="rounded-r-none border-r-0 cursor-pointer" asChild>
        <a href={primaryAction.href} target="_blank" rel="noopener noreferrer">
          <primaryAction.Icon className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">{primaryAction.label}</span>
        </a>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={variant}
            size="sm"
            className="rounded-l-none px-1.5 border-l border-l-current/20 cursor-pointer"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {viewTraceUrl && (
            <DropdownMenuItem asChild className="cursor-pointer">
              <a href={mastraRunUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                View Run
              </a>
            </DropdownMenuItem>
          )}
          {viewSessionUrl && (
            <DropdownMenuItem asChild className="cursor-pointer">
              <a href={viewSessionUrl} target="_blank" rel="noopener noreferrer">
                <Layers className="h-4 w-4 mr-2" />
                View Session
              </a>
            </DropdownMenuItem>
          )}
          {onApprove && (
            <DropdownMenuItem onSelect={onApprove} className="cursor-pointer">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {isApproved ? "Manually Approved" : "Approve"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
